import { useState, useEffect, useRef } from 'react'
import { useAskSamiksh } from '../../hooks/useAskSamiksh'
import type { QAPair } from '../../hooks/useAskSamiksh'
import type { Citation } from '../../gen/types'

export interface BoundAnchor {
  path: string
  startLine: number
  endLine: number
}

interface AskSamikshProps {
  reviewId: string
  revisionId?: string
  boundAnchor?: BoundAnchor | null
  onClearBoundAnchor?: () => void
  inputRef?: React.RefObject<HTMLInputElement | null>
}

function CitationPill({ citation }: { citation: Citation }) {
  return (
    <span
      className="inline-flex items-center text-[10px] font-mono text-fg-dim bg-canvas border border-border-subtle rounded-[3px] px-1.5 py-[1px]"
      title={citation.quote || undefined}
    >
      [{citation.kind}] {citation.ref}
    </span>
  )
}

function QABubble({
  pair,
  onFollowUp,
}: {
  pair: QAPair
  onFollowUp: (text: string) => void
}) {
  const { question, answer } = pair
  const lowConfidence = answer.confidence < 0.6

  return (
    <div className="flex flex-col gap-2 mb-4">
      {/* Question — right aligned */}
      <div className="flex gap-2 justify-end items-end">
        <div className="bg-accent-bg border border-accent-dim rounded-[10px] rounded-br-[3px] px-3 py-2 max-w-[80%] text-[12px] text-fg">
          <p className="m-0 leading-[1.55]">{question.text}</p>
        </div>
        <span aria-hidden className="text-[16px]">👤</span>
      </div>

      {/* Answer — left aligned */}
      <div className="flex gap-2 items-end">
        <span aria-hidden className="text-[16px] text-accent">⚡</span>
        <div className="bg-surface border border-border-default rounded-[10px] rounded-bl-[3px] px-3 py-2 max-w-[85%] text-[12px] text-fg flex flex-col gap-2">
          {lowConfidence && (
            <div className="text-[10px] text-sev-must-fix font-mono">
              ⚠ Low confidence ({Math.round(answer.confidence * 100)}%) — verify this answer
            </div>
          )}
          <p className="m-0 leading-[1.55]">{answer.text}</p>

          {answer.citations && answer.citations.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {answer.citations.map((c, i) => (
                <CitationPill key={i} citation={c} />
              ))}
            </div>
          )}

          {answer.followUpSuggestions && answer.followUpSuggestions.length > 0 && (
            <div className="flex flex-col gap-1.5 mt-1">
              <span className="text-[10px] uppercase tracking-[0.06em] text-fg-dim font-semibold">
                Suggested follow-ups:
              </span>
              <div className="flex flex-wrap gap-1.5">
                {answer.followUpSuggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    type="button"
                    className="btn btn-default text-[10px] normal-case"
                    onClick={() => onFollowUp(suggestion)}
                  >
                    {suggestion}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default function AskSamiksh({
  reviewId: _reviewId,
  revisionId,
  boundAnchor,
  onClearBoundAnchor,
  inputRef: externalInputRef,
}: AskSamikshProps) {
  const { history, ask } = useAskSamiksh(_reviewId)
  const [inputValue, setInputValue] = useState('')
  const historyEndRef = useRef<HTMLDivElement>(null)
  const localInputRef = useRef<HTMLInputElement>(null)
  const inputRef = externalInputRef ?? localInputRef

  useEffect(() => {
    if (history.length > 0) {
      historyEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [history])

  useEffect(() => {
    if (!ask.isPending && history.length > 0) {
      historyEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
  }, [ask.isPending, history.length])

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const text = inputValue.trim()
    if (!text || ask.isPending) return
    const params: Parameters<typeof ask.mutate>[0] = { text, revisionId }
    if (boundAnchor) {
      params.anchorPath = boundAnchor.path
      params.anchorStartLine = boundAnchor.startLine
      params.anchorEndLine = boundAnchor.endLine
      params.scopeHint = 'this-line'
    }
    ask.mutate(params, {
      onSuccess: () => {
        setInputValue('')
        inputRef.current?.focus()
      },
    })
  }

  function handleFollowUp(suggestion: string) {
    if (ask.isPending) return
    setInputValue(suggestion)
    inputRef.current?.focus()
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit(e as unknown as React.FormEvent)
    }
  }

  const isEmpty = history.length === 0 && !ask.isPending

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Scrollable Q&A history */}
      <div className="flex-1 min-h-0 overflow-auto px-3 py-3">
        {isEmpty && (
          <div className="flex flex-col items-center justify-center text-center gap-2 py-6 text-fg-dim">
            <span aria-hidden className="text-[24px] text-accent">⚡</span>
            <p className="m-0 text-[13px] text-fg-muted font-semibold">Ask samik.sh anything</p>
            <p className="m-0 text-[11px] max-w-[360px]">
              Ask about this review, specific code patterns, findings, or request
              an explanation of any change.
            </p>
          </div>
        )}

        {history.map((pair) => (
          <QABubble key={pair.question.id} pair={pair} onFollowUp={handleFollowUp} />
        ))}

        {ask.isPending && (
          <div className="flex gap-2 items-end">
            <span aria-hidden className="text-[16px] text-accent">⚡</span>
            <div className="bg-surface border border-border-default rounded-[10px] rounded-bl-[3px] px-3 py-2 inline-flex gap-1.5">
              <Dot delay="0s" />
              <Dot delay="0.2s" />
              <Dot delay="0.4s" />
            </div>
          </div>
        )}

        {ask.isError && (
          <div className="flex items-center gap-2 text-sev-blocking text-[12px] mt-2">
            <span aria-hidden>⚠</span>
            <span>
              {ask.error instanceof Error
                ? ask.error.message
                : 'Failed to get an answer. Please try again.'}
            </span>
          </div>
        )}

        <div ref={historyEndRef} aria-hidden />
      </div>

      {/* Input area */}
      <div className="border-t border-border-subtle bg-page">
        {boundAnchor && (
          <div
            data-testid="ask-bound-anchor-chip"
            className="flex items-center gap-1.5 mx-3 mt-2 px-2 py-1 rounded-[6px] bg-accent-bg border border-accent-dim text-[11px] font-mono text-accent w-fit max-w-[calc(100%-24px)]"
          >
            <span aria-hidden>📍</span>
            <span className="truncate" title={`${boundAnchor.path}:L${boundAnchor.startLine}-L${boundAnchor.endLine}`}>
              Bound to {boundAnchor.path.split('/').pop()}:L{boundAnchor.startLine}
              {boundAnchor.endLine !== boundAnchor.startLine ? `–L${boundAnchor.endLine}` : ''}
            </span>
            {onClearBoundAnchor && (
              <button
                type="button"
                onClick={onClearBoundAnchor}
                aria-label="Clear bound anchor"
                className="bg-transparent border-0 p-0 ml-1 text-fg-dim hover:text-fg cursor-pointer text-[12px] leading-none"
              >
                ×
              </button>
            )}
          </div>
        )}
        <form
          className="flex gap-2 items-center px-3 py-2"
          onSubmit={handleSubmit}
        >
          <input
            ref={inputRef}
            type="text"
            className="input rounded-full px-3.5 py-[6px] text-[12px]"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={ask.isPending ? 'Waiting for answer…' : 'Ask a question about this review…'}
            disabled={ask.isPending}
            autoComplete="off"
            spellCheck
          />
          <button
            type="submit"
            className="btn btn-send"
            disabled={ask.isPending || !inputValue.trim()}
            aria-label="Send"
          >
            {ask.isPending ? (
              <span
                className="w-[10px] h-[10px] border-2 border-accent border-t-transparent rounded-full inline-block"
                style={{ animation: 'spin 0.7s linear infinite' }}
                aria-hidden
              />
            ) : (
              '↑'
            )}
          </button>
        </form>
      </div>
    </div>
  )
}

function Dot({ delay }: { delay: string }) {
  return (
    <span
      className="w-[5px] h-[5px] rounded-full bg-accent"
      style={{
        animation: 'typingBounce 1.2s ease-in-out infinite',
        animationDelay: delay,
      }}
    />
  )
}
